import React, { useState, useEffect } from 'react';
import { useSocket } from '../contexts/SocketContext';
import api from '../services/api';
import AppointmentCard from '../components/AppointmentCard';
import CreateAppointmentModal from '../components/CreateAppointmentModal';

const Appointments = () => {
  const { socket } = useSocket();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/appointments');
      setAppointments(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  useEffect(() => {
    if (!socket) return;

    // Join appointments-global room
    socket.emit('join-appointments-global');

    // Listen for real-time events
    const handleAppointmentCreated = (appointment) => {
      setAppointments(prev => {
        // Add new appointment and sort by start_time
        const updated = [...prev, appointment];
        return updated.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      });
    };

    const handleAppointmentUpdated = (appointment) => {
      setAppointments(prev =>
        prev.map(a => (a.id === appointment.id ? appointment : a))
      );
    };

    const handleAppointmentDeleted = (data) => {
      setAppointments(prev => prev.filter(a => a.id !== data.id));
    };

    const handleAppointmentForked = (data) => {
      // Show notification
      alert(`The creator updated appointment. View the new version?`);
      // Could also navigate to new appointment
    };

    socket.on('appointment-created', handleAppointmentCreated);
    socket.on('appointment-updated', handleAppointmentUpdated);
    socket.on('appointment-deleted', handleAppointmentDeleted);
    socket.on('appointment-forked', handleAppointmentForked);

    return () => {
      socket.emit('leave-appointments-global');
      socket.off('appointment-created', handleAppointmentCreated);
      socket.off('appointment-updated', handleAppointmentUpdated);
      socket.off('appointment-deleted', handleAppointmentDeleted);
      socket.off('appointment-forked', handleAppointmentForked);
    };
  }, [socket]);

  const handleAppointmentUpdate = (updatedAppointment) => {
    setAppointments(prev =>
      prev.map(a => (a.id === updatedAppointment.id ? updatedAppointment : a))
    );
  };

  const handleAppointmentDelete = (deletedId) => {
    setAppointments(prev => prev.filter(a => a.id !== deletedId));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Loading appointments...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Appointments</h1>
            <p className="text-gray-600 mt-2">
              Propose work sessions and see what others are working on
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow-md"
          >
            + Create Appointment
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
          {error}
          <button
            onClick={fetchAppointments}
            className="ml-4 text-sm underline hover:text-red-900"
          >
            Retry
          </button>
        </div>
      )}

      {/* Appointments List */}
      {appointments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-xl text-gray-600 mb-4">No upcoming appointments</p>
          <p className="text-gray-500 mb-6">
            Be the first to create an appointment and invite others to join!
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
          >
            Create First Appointment
          </button>
        </div>
      ) : (
        <div>
          {appointments.map(appointment => (
            <AppointmentCard
              key={appointment.id}
              appointment={appointment}
              onUpdate={handleAppointmentUpdate}
              onDelete={handleAppointmentDelete}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      <CreateAppointmentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => setShowCreateModal(false)}
      />
    </div>
  );
};

export default Appointments;
